import React, { useCallback, useState } from 'react';
import { Typography, Tooltip, Box } from '@mui/material';
import {
  VisibilityOutlined as VisibilityIcon,
  EditOutlined as EditIcon,
  LockOutlined as LockIcon,
  LockOpenOutlined as LockOpenIcon,
  ReceiptOutlined as ReceiptIcon,
  ZoomInOutlined as ZoomInIcon,
} from '@mui/icons-material';
import { InventoryItem } from '../../../../types/inventory';
import { useInventoryQueryTable } from '../InventoryQueryTable.hooks';
import { mapWarehouseStatus, mapInventoryAvailability } from '../../../common/status';
import { usePermission } from '../../../../hooks/usePermission';
import { useInventoryBranchPermissions } from '../../../../hooks/useInventoryBranchPermissions';
import { useInventoryGeneralRequestPermissions } from '../../../../hooks/useInventoryRequestPermissions';
import { useInventoryWarehousePermissions } from '../../../../hooks/useInventoryWarehousePermissions';
import { StatusChip, ViewButton } from '../InventoryQueryTable.styles';
import { ActionButton } from '../../../common/Table/Table.styles';
import EmptyStateMessage from '../../../common/EmptyStateMessage';
import {
  INV_AVAILABILITY_BLOCKED_LABEL,
  INV_STATUS_BRANCH_LABEL,
  INV_STATUS_WAREHOUSE_LABEL,
  isAvailabilityWrittenOff,
  isEligibleForWarehouseBlock,
  isSelectableForWarehouseTransfer,
} from '../../../../constants/inventory-status-labels';
import { INVENTORY_PERMISSIONS } from '../../../../constants/permissions.constants';
import { formatPieceSalePrice } from '../../../../utils/formatters/money';
import ImageModal from '../../../ui/ImageModal';
import ProductImagePlaceholder from '../../../ui/ProductImagePlaceholder';
import { isUsableProductImage } from '../../../../utils/productImage';
import {
  GridContainer,
  InventoryCard,
  ImageWrapper,
  ItemThumbnail,
  CardCheckbox,
  ExpandImageButton,
  ImageOverlay,
  CardContent,
  CardActionsBar,
  EmptyStateContainer,
} from './InventoryGrid.styles';

export type InventoryGridVariant = 'warehouse' | 'branch';

export interface InventoryGridBaseProps {
  variant: InventoryGridVariant;
  items: InventoryItem[];
  onViewDetails: (id: string) => void;
  selectedItems: string[];
  onSelectItems: (items: string[]) => void;
  onTransferSelected?: (selectedItems: string[]) => void;
  /** Imprimir etiqueta unitaria (almacén o sucursal). */
  onPrintLabel?: (item: InventoryItem) => void;
  /** Oculta ubicación/sucursal en tarjeta (p. ej. Gestión en Almacén). */
  hideLocationColumn?: boolean;
  /** Inventario general: selección para solicitud. */
  enableRequestSelection?: boolean;
  lockedOriginLocationName?: string | null;
}

export interface InventoryGridWarehouseProps extends InventoryGridBaseProps {
  variant: 'warehouse';
  onEditItem?: (id: string) => void;
  onReserveItem?: (id: string) => void;
}

export interface InventoryGridBranchProps extends InventoryGridBaseProps {
  variant: 'branch';
  onEditItem?: (id: string) => void;
  onReserveItem?: (id: string) => void;
}

export type InventoryGridProps = InventoryGridWarehouseProps | InventoryGridBranchProps;

const getThumbnailSrc = (item: InventoryItem): string | undefined => {
  const p = item.product;
  if (!p) return undefined;
  const thumb = (p as { thumbnailUrl?: string | null }).thumbnailUrl;
  return [thumb, p.imagePath].filter(isUsableProductImage)[0];
};

/** URL preferente para el visor a pantalla completa (misma prioridad que ProductImagePreview). */
const getExpandedImageUrl = (item: InventoryItem): string | undefined => {
  const p = item.product;
  if (!p) return undefined;
  const thumb = (p as { thumbnail?: string | null }).thumbnail;
  return [p.imagePath, thumb, p.thumbnailUrl].filter(isUsableProductImage)[0];
};

const isSelectableWarehouse = (item: InventoryItem) => isSelectableForWarehouseTransfer(item);
const isSelectableBranch = (item: InventoryItem) =>
  item.status === INV_STATUS_BRANCH_LABEL && item.availabilityStatus === 'Disponible';

const InventoryGridComponent: React.FC<InventoryGridProps> = (props) => {
  const {
    variant,
    items,
    onViewDetails,
    selectedItems,
    onSelectItems,
    onTransferSelected,
    onPrintLabel,
    hideLocationColumn = false,
    enableRequestSelection = false,
    lockedOriginLocationName = null,
  } = props;

  const canViewWarehouse = usePermission('inventory.inventory-warehouse.view');
  const warehousePerms = useInventoryWarehousePermissions();
  const branchPerms = useInventoryBranchPermissions();
  const { canCreateRequest } = useInventoryGeneralRequestPermissions();
  const canViewInventoryGeneral = usePermission(INVENTORY_PERMISSIONS.GENERAL.VIEW);

  const showRequestCheckbox = enableRequestSelection && canCreateRequest;

  const [expandedImage, setExpandedImage] = useState<{ url: string; alt: string } | null>(null);

  const handleCloseExpandedImage = useCallback(() => setExpandedImage(null), []);

  const { handleSelectItem, getCheckboxStyles, getItemSelectionState } = useInventoryQueryTable({
    items,
    pagination: { currentPage: 1, totalPages: 1, itemsPerPage: 10, totalItems: items.length },
    selectedItems,
    onSelectItems: (ids) => onSelectItems(ids),
    onItemsPerPageChange: () => {},
    isInventoryBranchQuery: variant === 'branch' && !showRequestCheckbox,
    isInventoryGeneralRequest: showRequestCheckbox,
    lockedOriginLocationName,
    isAdmin: true,
  });

  const isSelectable = (item: InventoryItem) => {
    if (showRequestCheckbox) {
      return getItemSelectionState(item).selectable;
    }
    return variant === 'warehouse' ? isSelectableWarehouse(item) : isSelectableBranch(item);
  };
  const showCheckbox =
    showRequestCheckbox ||
    (variant === 'warehouse'
      ? !!onTransferSelected
      : !!(onTransferSelected && branchPerms.canTransfer));

  if (variant === 'warehouse' && !canViewWarehouse) return null;
  if (variant === 'branch' && !branchPerms.canView && !canViewInventoryGeneral) return null;

  const handleCardClick = (e: React.MouseEvent, item: InventoryItem) => {
    if ((e.target as HTMLElement).closest("button, [role='button'], .MuiCheckbox-root")) return;
    onViewDetails(item.id);
  };

  if (items.length === 0) {
    return (
      <GridContainer>
        <EmptyStateContainer>
          <EmptyStateMessage message="No se encontraron piezas" minHeight={200} />
        </EmptyStateContainer>
      </GridContainer>
    );
  }

  return (
    <>
      <GridContainer>
        {items.map((item) => {
          const selected = selectedItems.includes(item.id);
          const selectable = isSelectable(item);
          const statusChip = mapWarehouseStatus(item.status);
          const availabilityChip = mapInventoryAvailability(item.availabilityStatus);
          const expandedUrl = getExpandedImageUrl(item);
          const thumbnailSrc = getThumbnailSrc(item);
          const canExpandImage = Boolean(expandedUrl);

          return (
            <InventoryCard
              key={item.id}
              onClick={(e) => handleCardClick(e, item)}
              sx={{
                outline: selected ? '2px solid #2563eb' : undefined,
              }}
            >
              <ImageWrapper>
                {showCheckbox && (
                  <CardCheckbox
                    size="small"
                    checked={selected}
                    disabled={!selectable}
                    onChange={() => handleSelectItem(item.id)}
                    onClick={(e) => e.stopPropagation()}
                    sx={getCheckboxStyles(selectable)}
                  />
                )}
                {canExpandImage && (
                  <Tooltip title="Ver imagen completa" arrow>
                    <ExpandImageButton
                      size="small"
                      aria-label={`Ver imagen completa de ${item.name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (expandedUrl) {
                          setExpandedImage({ url: expandedUrl, alt: item.name });
                        }
                      }}
                    >
                      <ZoomInIcon sx={{ fontSize: 18 }} />
                    </ExpandImageButton>
                  </Tooltip>
                )}
                {thumbnailSrc ? (
                  <ItemThumbnail src={thumbnailSrc} alt={item.name} loading="lazy" />
                ) : (
                  <ProductImagePlaceholder variant="fill" />
                )}
                <ImageOverlay>
                  <Typography variant="subtitle2" fontWeight={700} noWrap>
                    {item.name}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    {item.id}
                  </Typography>
                </ImageOverlay>
              </ImageWrapper>
              <CardContent>
                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                  {formatPieceSalePrice(item.price)}
                </Typography>
                <StatusChip label={statusChip.label} size="small" color={statusChip.color} />
                {variant === 'warehouse' && (
                  <Box sx={{ mt: 0.5 }}>
                    <StatusChip
                      label={availabilityChip.label}
                      size="small"
                      color={availabilityChip.color}
                    />
                  </Box>
                )}
                {!hideLocationColumn && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    noWrap
                    sx={{ mt: 0.5 }}
                  >
                    {item.locationName || '—'}
                  </Typography>
                )}
              </CardContent>
              <CardActionsBar onClick={(e) => e.stopPropagation()}>
                <Tooltip title="Ver detalles" arrow>
                  <ViewButton size="small" onClick={() => onViewDetails(item.id)}>
                    <VisibilityIcon fontSize="small" />
                  </ViewButton>
                </Tooltip>
                {variant === 'branch' &&
                  props.onEditItem &&
                  branchPerms.canEdit &&
                  !isAvailabilityWrittenOff(item.availabilityStatus) && (
                    <Tooltip title="Editar pieza" arrow>
                      <ActionButton size="small" onClick={() => props.onEditItem!(item.id)}>
                        <EditIcon fontSize="small" />
                      </ActionButton>
                    </Tooltip>
                  )}
                {variant === 'warehouse' &&
                  props.onEditItem &&
                  warehousePerms.canEdit &&
                  !isAvailabilityWrittenOff(item.availabilityStatus) && (
                    <Tooltip title="Editar pieza" arrow>
                      <ActionButton size="small" onClick={() => props.onEditItem!(item.id)}>
                        <EditIcon fontSize="small" />
                      </ActionButton>
                    </Tooltip>
                  )}
                {variant === 'branch' &&
                  props.onReserveItem &&
                  item.availabilityStatus === 'Disponible' &&
                  branchPerms.canReserve && (
                    <Tooltip title="Bloquear pieza" arrow>
                      <ActionButton size="small" onClick={() => props.onReserveItem!(item.id)}>
                        <LockIcon fontSize="small" />
                      </ActionButton>
                    </Tooltip>
                  )}
                {variant === 'branch' &&
                  props.onReserveItem &&
                  item.availabilityStatus === INV_AVAILABILITY_BLOCKED_LABEL &&
                  branchPerms.canUnreserve && (
                    <Tooltip title="Liberar bloqueo" arrow>
                      <ActionButton
                        size="small"
                        colorVariant="error"
                        onClick={() => props.onReserveItem!(item.id)}
                      >
                        <LockOpenIcon fontSize="small" />
                      </ActionButton>
                    </Tooltip>
                  )}
                {variant === 'warehouse' &&
                  props.variant === 'warehouse' &&
                  props.onReserveItem &&
                  isEligibleForWarehouseBlock(item.status) &&
                  item.availabilityStatus === 'Disponible' &&
                  warehousePerms.canReserve && (
                    <Tooltip title="Bloquear pieza (impide transferencia)" arrow>
                      <ActionButton size="small" onClick={() => props.onReserveItem!(item.id)}>
                        <LockIcon fontSize="small" />
                      </ActionButton>
                    </Tooltip>
                  )}
                {variant === 'warehouse' &&
                  props.variant === 'warehouse' &&
                  props.onReserveItem &&
                  isEligibleForWarehouseBlock(item.status) &&
                  item.availabilityStatus === INV_AVAILABILITY_BLOCKED_LABEL &&
                  warehousePerms.canUnreserve && (
                    <Tooltip title="Liberar bloqueo" arrow>
                      <ActionButton
                        size="small"
                        colorVariant="error"
                        onClick={() => props.onReserveItem!(item.id)}
                      >
                        <LockOpenIcon fontSize="small" />
                      </ActionButton>
                    </Tooltip>
                  )}
                {onPrintLabel && !isAvailabilityWrittenOff(item.availabilityStatus) && (
                  <Tooltip title="Imprimir etiqueta" arrow>
                    <ViewButton size="small" onClick={() => onPrintLabel(item)}>
                      <ReceiptIcon fontSize="small" />
                    </ViewButton>
                  </Tooltip>
                )}
              </CardActionsBar>
            </InventoryCard>
          );
        })}
      </GridContainer>
      <ImageModal
        isOpen={expandedImage !== null}
        onClose={handleCloseExpandedImage}
        imageUrl={expandedImage?.url ?? ''}
        altText={expandedImage?.alt ?? 'Imagen del producto'}
      />
    </>
  );
};

export default InventoryGridComponent;
